<template>
  <div class="flex flex-col w-full h-full gap-2 overflow-y-auto">
    <span v-if="limitedServices?.length > 0" class="subtitle-bold section">שירותים שנוספו</span>
    <div v-if="limitedServices?.length > 0" class="flex gap-2 flex-wrap">
      <div
        v-for="service of limitedServices"
        :key="service.id"
        class="p-chip p-chip-text flex-none flex gap-2 bg-[#dee2e6] p-2 rounded-2xl text-[#495057] justify-center items-center"
      >
        <span>{{ service.name }}</span>
        <i
          class="pi-times-circle pi"
          @click="deleteLimitedService(service)"
        />
      </div>
    </div>
    <div v-if="hasNoExclusiveServices"
         class="flex flex-col gap-2 items-center text-center rounded-lg border border-dashed p-6 mt-2">
      <i class="pi pi-sparkles text-primary" style="font-size: 1.25rem" />
      <span class="font-medium">עדיין לא הגדרתם שירותים בלעדיים</span>
      <span class="text-sm text-gray-500">
        כדי להוסיף שירות בלעדי ללקוח, היכנסו להגדרות השירות והפעילו את
        "שירות ללקוחות נבחרים בלבד". לאחר מכן תוכלו לבחור אותו כאן.
      </span>
    </div>

    <template v-else>
      <span class="subtitle-bold section mt-2">בחירת שירות</span>
      <Select
        v-model="selectedService"
        :options="services"
        optionLabel="name"
        optionGroupLabel="label"
        optionGroupChildren="items"
        placeholder="בחירת שירות"
        emptyMessage="כל השירותים הבלעדיים כבר נוספו ללקוח"
        class="w-full"/>
    </template>

    <drawer-footer
      :approveLoading="loading"
      :approveDisable="!selectedService"
      approveLabel="הוספת שירות בלעדי"
      @approve-click="clickCTA"
      @cancel-click="$emit('close')"
    />
  </div>
</template>

<script lang="ts" setup>
import {computed, onMounted, ref} from 'vue';
import {useRoute} from "vue-router";
import {CustomersApi} from "@app/api/admin";
import {IClient, IService} from "@torim-app/types";
import {useAdminStore} from "@app/stores";
import Select from 'primevue/select';
import {useToast} from "vue-toastification";
import { trackEvent } from '@app/composables/posthog';

const emit = defineEmits(['close']);
const route = useRoute();
const adminStore = useAdminStore();
const toast = useToast();

const clientId = route.params.id as string;
const loading = ref(false);
const selectedService = ref<IService>();
const limitedServices = ref<Array<IService>>([]);

const isMainClient = computed(() => adminStore.getMainClient);
const canManageOthers = computed(() => adminStore.getCanManageOthers);
const providers = computed<Array<Pick<IClient, 'userId' | 'fullName' | 'profileImage'>>>(() => canManageOthers.value ? adminStore.getProviders : []);
// Every exclusive service the business has defined, minus the ones already granted.
const availableServices = computed<Array<IService>>(() => {
  const addedIds = new Set(limitedServices.value.map(s => s.id));
  return adminStore.getServices.filter(service =>
    service.visible && service.selectedClientsOnly && !addedIds.has(service.id));
});

// The business has no exclusive services at all — the owner has to turn the toggle on in the
// service settings first. Without this the drawer just showed an empty dropdown with no hint.
const hasNoExclusiveServices = computed(() =>
  availableServices.value.length === 0 && limitedServices.value.length === 0);

const services = computed<Array<{ label: string; items: IService[] }>>(() => {
  const groups = (providers.value ?? []).map(p => ({
    label: p.fullName, items: availableServices.value.filter(s => s.providingClients?.includes(p.userId))
  })).filter(s => s.items.length > 0);

  // A service with no provider assigned (or one belonging to a provider we can't see) matches no
  // group. Collect the leftovers so grouping can never make a valid service unpickable.
  const groupedIds = new Set(groups.flatMap(g => g.items.map(s => s.id)));
  const ungrouped = availableServices.value.filter(s => !groupedIds.has(s.id));
  if (ungrouped.length)
    groups.push({ label: groups.length ? 'ללא נותן שירות משויך' : 'שירותים בלעדיים', items: ungrouped });

  return groups;
})

const clickCTA = (): void => {
  trackEvent('customer_service_limit_updated');
  if (!selectedService.value) return;

  const service = selectedService.value;
  loading.value = true;
  CustomersApi.addLimitedServiceToClient(clientId, {
    serviceId: service.id,
  })
    .then(() => {
      limitedServices.value = [...limitedServices.value, service];
      selectedService.value = undefined;
      toast.info('השירות נוסף בהצלחה');
    })
    .catch((e) => {
      console.error(e);
      toast.error('לא הצלחנו להוסיף את השירות, אנא נסו שוב');
    })
    .finally(() => {
      loading.value = false;
    });
};

const deleteLimitedService = (serviceToRemove: IService) => {
  CustomersApi.removeLimitedServiceToClient(clientId, {
    serviceId: serviceToRemove.id,
  }).then(() => {
    limitedServices.value = limitedServices.value.filter(s => s.id !== serviceToRemove.id);
    toast.info('השירות הוסר בהצלחה')
  }).catch((e) => {
    console.log(e);
    toast.info('לא הצלחנו להסיר את השירות, אנא נסו שוב')
  });
};

onMounted(async () => {
  const _limitedServices = await CustomersApi.fetchCustomerLimitedServices(clientId);
  limitedServices.value =
    (_limitedServices
      ? adminStore?.businessSiteData?.services?.filter((s) =>
        _limitedServices.includes(s.id)
      )
      : []) ?? [];
})
</script>
